import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';

const RevenueContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

const Section = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const WelcomeText = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const RewardText = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  margin-top: 1rem;
  padding: 1rem;
  background-color: rgba(254, 189, 35, 0.1);
  border-radius: 4px;
  border-left: 3px solid ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem;
  }
`;

const PointsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PointCard = styled.div`
  background-color: #161616;
  border-radius: 4px;
  padding: 1rem;
  border: 1px solid #333;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PointType = styled.div`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const PointValue = styled.div`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const InfoText = styled.div`
  line-height: 1.6;
`;

const PointsTable = styled.div`
  margin-top: 1rem;
  padding: 1rem 0;
`;

const PointRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #222;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }
`;

const PointItem = styled.div`
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
`;

const PointValue2 = styled.div`
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
`;

const RevenueSharePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  return (
    <DashboardLayout activeMenuItem="revenue">
      <RevenueContainer>
        <Title>Revenue Share</Title>
        
        <Section>
          <WelcomeText>
            Welcome to $Voovo Revenue share portal. Here you will be able to see your rewards 
            based on the Bot mentions and usage. Revenue share counting is Live. Data will be 
            refreshed once every 48 hours.
          </WelcomeText>
          
          <RewardText>
            Current Total Reward Share from Voovo X Bot - $0 (Updating every 48 hours based on usage)
          </RewardText>
        </Section>
        
        <Section>
          <SectionTitle>Your Points</SectionTitle>
          
          <PointsGrid>
            <PointCard>
              <PointType>Views</PointType>
              <PointValue>0</PointValue>
            </PointCard>
            
            <PointCard>
              <PointType>Retweets</PointType>
              <PointValue>0</PointValue>
            </PointCard>
            
            <PointCard>
              <PointType>Likes</PointType>
              <PointValue>0</PointValue>
            </PointCard>
            
            <PointCard>
              <PointType>Quote Retweets</PointType>
              <PointValue>0</PointValue>
            </PointCard>
          </PointsGrid>
        </Section>
        
        <Section>
          <SectionTitle>Information</SectionTitle>
          
          <InfoText>
            The revenue share is based on your Bot interactions, how many views and engagement 
            your posts get, by using our Utility. Do not spam, but use it with a good cause 
            under influencer tweets or by creating threads and mentioning our Bot with function 
            requests. Here are the point calculations based on your post engagements.
          </InfoText>
          
          <PointsTable>
            <PointRow>
              <PointItem>Views</PointItem>
              <PointValue2>1 point</PointValue2>
            </PointRow>
            
            <PointRow>
              <PointItem>Retweet</PointItem>
              <PointValue2>2 points</PointValue2>
            </PointRow>
            
            <PointRow>
              <PointItem>Likes</PointItem>
              <PointValue2>2 points</PointValue2>
            </PointRow>
            
            <PointRow>
              <PointItem>Quote Retweet</PointItem>
              <PointValue2>3 points</PointValue2>
            </PointRow>
          </PointsTable>
        </Section>
      </RevenueContainer>
    </DashboardLayout>
  );
};

export default RevenueSharePage; 